import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Button } from "@mui/material";
import GenericFormPopup from "../GenericFormPopup/GenericFormPopup";
import {
  trailerFieldConfiguration,
  trailerInitialValues,
} from "../../../formConfigs/Fleet/trailerFieldConfig";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import { fetchOwnerCompanies } from "../TruckPopup/TruckPopup";
import { getId } from "../../HR/UpdateEmployeePopup/UpdateEmployeePopup";
import { fetchLoans } from "../LoanTable/LoanTable";

const TrailerPopup = () => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [prefilledValues, setPrefilledValues] = useState(trailerInitialValues);
  const [shouldReset, setShouldReset] = useState(false);
  const queryClient = useQueryClient();
  const { token } = useAuth();

  useEffect(() => {
    if (shouldReset) {
      setShouldReset(false);
    }
  }, [shouldReset]);

  const { data: ownerCompanies } = useQuery({
    queryKey: ["onwerCompanies"],
    queryFn: () => fetchOwnerCompanies(token),
    enabled: !!open,
  });

  const { data: loansData } = useQuery({
    queryKey: ["loans"],
    queryFn: () =>
      fetchLoans(token, new Date().toISOString().slice(0, 16), true).then(
        (response) =>
          response.map((loan: any) => ({
            label: `${loan.loanNumber} - ${loan.lenderName} - $${loan.monthlyPayment}`,
            id: loan.id,
          }))
      ),
  });

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setPrefilledValues(trailerInitialValues);
  };
  const handleSnackbarClose = () => setStatus("idle");

  const mutation = useMutation({
    mutationFn: (data: any) =>
      axios.post(`${apiVariable}/trailer`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    onSuccess: () => {
      setStatus("success");
      queryClient.invalidateQueries({ queryKey: ["trailersTable"] });
      queryClient.invalidateQueries({ queryKey: ["assets", "Trailer"] });

      handleClose();
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response.data || "Failed to submit trailer");
    },
  });

  const submitHandler = (data: any) => {
    const finalData = {
      ...data,
      companyOwnerId: getId(data.companyOwnerId),
      loanId: getId(data.loanId),
    };

    mutation.mutate(finalData);
  };

  return (
    <>
      <Button variant="contained" color="primary" onClick={handleOpen}>
        Add Trailer
      </Button>
      <GenericFormPopup
        formFields={trailerFieldConfiguration}
        initialValues={prefilledValues}
        title="Add Trailer"
        onSubmit={submitHandler}
        open={open}
        onClose={handleClose}
        loading={mutation.isPending}
        shouldReset={shouldReset}
        autocompleteData={{
          ownerCompanies: ownerCompanies,
          loansData: loansData,
        }}
      />
      <StatusSnackbar
        status={status}
        successMessage="Trailer added successfully"
        errorMessage={errorMessage}
        handleClose={handleSnackbarClose}
      />
    </>
  );
};

export default TrailerPopup;
